import React, { useEffect, useState } from "react";
import { Container, Nav, Modal, Form, Button, Row, Col } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { message, Modal as AntModal } from "antd";
import { FiThumbsUp } from "react-icons/fi";
import { BiRecycle } from "react-icons/bi";
import Footer from "../../../components/superAdmin/footer/Footer";
import SubAdminTable from "../../../components/subadmin/SubAdminTable";
import ShowDetailsModal from "../../../common/ShowDetailsModal";
import SubAdminHeader from "../components/SubAdminHeader";

const SubAdminBuyers = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState(null);
    const [activeTab, setActiveTab] = useState('buyersRequestList');

    // Modals state
    const [showDetails, setShowDetails] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showEdit, setShowEdit] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);

    const tabs = [
        { key: 'buyersRequestList', title: 'Hosted Buyers Request List', endpoint: 'requests', path: '/subadmin/buyers/requests' },
        { key: 'buyersInvitedList', title: 'Invited Hosted Buyers List', endpoint: 'invited', path: '/subadmin/buyers/invited' },
        { key: 'buyersHostedRecycledList', title: 'Hosted Recycled Buyers List', endpoint: 'recycled', path: '/subadmin/buyers/recycled' },
        { key: 'buyersOldList', title: 'Old Buyers List', endpoint: 'old', path: '/subadmin/buyers/old' }
    ];


    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('jff_token');
            const dashResponse = await axios.get(`${process.env.REACT_APP_API_END_POINT}/subadmin/dashboard`, {
                headers: { token }
            });

            const buyersPerms = dashResponse.data.permissions.buyersList;
            setPermissions(buyersPerms);

            const currentTabKey = tabs.find(t => t.path === location.pathname)?.key || activeTab;

            if (!buyersPerms[currentTabKey]?.enabled || !buyersPerms[currentTabKey]?.read) {
                const firstEnabled = tabs.find(t => buyersPerms[t.key]?.enabled && buyersPerms[t.key]?.read);
                if (firstEnabled) {
                    navigate(firstEnabled.path);
                    return;
                } else {
                    navigate("/subadmin/dashboard");
                    return;
                }
            }

            const currentTab = tabs.find(t => t.key === currentTabKey);
            const response = await axios.get(`${process.env.REACT_APP_API_END_POINT}/subadmin/buyers/${currentTab.endpoint}`, {
                headers: { token }
            });
            setData(response.data.data);
        } catch (error) {
            console.error("Error fetching buyers:", error);
            if (error.response && error.response.status === 403) {
                navigate("/subadmin/dashboard");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [location.pathname]);

    useEffect(() => {
        const matchingTab = tabs.find(t => t.path === location.pathname);
        if (matchingTab) {
            setActiveTab(matchingTab.key);
        }
    }, [location]);

    // Action Handlers
    const handleView = (item) => {
        setSelectedItem(item);
        setShowDetails(true);
    };

    const handleEdit = (item) => {
        setEditForm({ ...item });
        setShowEdit(true);
    };

    const handleUpdate = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('jff_token');
            const formData = new FormData();
            Object.keys(editForm).forEach(key => {
                if (key !== 'photo' && key !== 'id_proof_front' && key !== 'id_proof_back') {
                    formData.append(key, editForm[key]);
                }
            });
            formData.append('id', editForm._id);

            await axios.post(`${process.env.REACT_APP_API_END_POINT}/update-visitor`, formData, {
                headers: { token, barcode: editForm.barcode }
            });
            message.success("Updated successfully");
            setShowEdit(false);
            fetchData();
        } catch (error) {
            message.error("Failed to update");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (id) => {
        AntModal.confirm({
            title: 'Are you sure you want to delete this buyer?',
            content: 'This action cannot be undone.',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('jff_token');
                    await axios.post(`${process.env.REACT_APP_API_END_POINT}/delete-visitor`, { id }, {
                        headers: { token }
                    });
                    message.success("Deleted successfully");
                    fetchData();
                } catch (error) {
                    message.error("Failed to delete");
                }
            }
        });
    };

    const handleApprove = async (item) => {
        try {
            const token = localStorage.getItem('jff_token');
            const response = await axios.post(`${process.env.REACT_APP_API_END_POINT}/update-exhibitor-vip-guest`, {
                id: item._id,
                approved_status: "true",
                status: ""
            }, { headers: { token } });

            if (response.data.messageData) {
                message.error(response.data.messageData);
            } else {
                message.success("Approved successfully");
            }
            fetchData();
        } catch (error) {
            message.error("Failed to approve");
        }
    };

    const handleRecycle = async (item) => {
        try {
            const token = localStorage.getItem('jff_token');
            await axios.post(`${process.env.REACT_APP_API_END_POINT}/update-exhibitor-vip-guest`, {
                id: item._id,
                status: "1",
                approved_status: ""
            }, { headers: { token } });
            message.success("Recycled successfully");
            fetchData();
        } catch (error) {
            message.error("Failed to recycle");
        }
    };

    const handleStatusChange = async (item) => {
        try {
            const token = localStorage.getItem('jff_token');
            let newStatus = item.status === "active" ? "inactive" : "active";
            
            await axios.post(`${process.env.REACT_APP_API_END_POINT}/update-visitor`, 
                { 
                    id: item._id, 
                    status: newStatus,
                    visitor_name: item.visitor_name,
                    mobile_number: item.mobile_number,
                    barcode: item.barcode 
                },
                { headers: { token } }
            );
            
            message.success("Status updated successfully");
            fetchData();
        } catch (error) {
            console.error("Error updating status:", error);
            message.error("Failed to update status");
        }
    };

    const getExtraActions = (item) => {
        const actions = [];
        if (activeTab === 'buyersRequestList' && item.approved_status === 'false') {
            actions.push({
                label: 'Approve',
                icon: <FiThumbsUp />,
                color: '#000',
                onClick: handleApprove
            });
        }
        if (activeTab !== 'buyersHostedRecycledList') {
            actions.push({
                label: 'Recycle',
                icon: <BiRecycle />,
                color: '#000',
                onClick: handleRecycle
            });
        }
        return actions;
    };

    const currentPerms = permissions?.[activeTab];

    return (
        <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
            <SubAdminHeader />

            <Container fluid className="px-0 py-5">
                <div className="text-center mb-4">
                    <h2 className="form-heading">
                        {tabs.find(t => t.key === activeTab)?.title || 'Buyers Management'} - {data.length}
                    </h2>
                </div>


                <SubAdminTable
                    data={data}
                    columns={currentPerms?.columns || []}
                    loading={loading}
                    writeAccess={currentPerms?.write}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onStatusChange={handleStatusChange}
                    extraActions={getExtraActions}
                    tableName={`Buyers ${tabs.find(t => t.key === activeTab)?.title || 'List'}`}
                />
            </Container>

            <ShowDetailsModal
                showAllDetails={showDetails}
                setShowAllDetails={setShowDetails}
                data={selectedItem}
                allowedColumns={currentPerms?.columns}
            />

            <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Buyer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        value={editForm.visitor_name || ''}
                                        onChange={(e) => setEditForm({ ...editForm, visitor_name: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Mobile</Form.Label>
                                    <Form.Control
                                        value={editForm.mobile_number || ''}
                                        onChange={(e) => setEditForm({ ...editForm, mobile_number: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Company</Form.Label>
                                    <Form.Control
                                        value={editForm.company_name || ''}
                                        onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        value={editForm.email || ''}
                                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>City</Form.Label>
                                    <Form.Control
                                        value={editForm.city || ''}
                                        onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Designation</Form.Label>
                                    <Form.Control
                                        value={editForm.designation || ''}
                                        onChange={(e) => setEditForm({ ...editForm, designation: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleUpdate} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Footer />
        </div>
    );
};

export default SubAdminBuyers;
